import { Component, Input, Output, EventEmitter, inject, OnChanges, OnDestroy } from '@angular/core';
import { CommonModule } from '@angular/common';
import { MatButtonModule } from '@angular/material/button';
import { MatIconModule } from '@angular/material/icon';
import { ThemeService } from '../../services/theme.service';
import { SidenavService } from '../../services/sidenav.service';

export interface MobileAction {
    id: string;
    label: string;
    icon: string;
    isPrimary?: boolean;
    disabled?: boolean;
}

@Component({
    selector: 'lib-mobile-action-bar',
    standalone: true,
    imports: [CommonModule, MatButtonModule, MatIconModule],
    template: `
    <div class="mobile-action-bar sm:hidden" *ngIf="(isMobile$ | async) && actions.length > 0">

        <div class="actions-container">
            <ng-container *ngFor="let action of actions">
                <button *ngIf="!action.isPrimary" 
                        class="action-btn" 
                        [class.active]="activeActionId === action.id"
                        [disabled]="action.disabled"
                        (click)="onActionClick(action)">
                    <mat-icon>{{ action.icon }}</mat-icon>
                    <span class="action-label">{{ action.label }}</span>
                </button>

                <div *ngIf="action.isPrimary" class="primary-action-wrapper">
                    <button mat-fab color="primary" 
                            class="primary-fab"
                            [disabled]="action.disabled"
                            (click)="onActionClick(action)">
                        <mat-icon>{{ action.icon }}</mat-icon>
                    </button>
                    <span class="action-label primary-label">{{ action.label }}</span>
                </div>
            </ng-container>
            
            <!-- Allow custom projection if needed -->
            <ng-content></ng-content>
        </div>
    </div>
  `,
    styles: [`
    .mobile-action-bar {
        position: fixed;
        bottom: 0;
        left: 0;
        right: 0;
        height: 80px;
        background: var(--mat-sys-surface-container-high);
        border-top: 1px solid rgba(var(--mat-sys-outline), 0.1);
        z-index: 1000;
        display: flex;
        align-items: center;
        justify-content: center;
        padding-bottom: env(safe-area-inset-bottom);
        box-shadow: 0 -4px 20px rgba(0, 0, 0, 0.1);
    }

    .actions-container {
        display: flex;
        align-items: flex-end;
        justify-content: space-around;
        width: 100%;
        max-width: 500px;
        padding: 0 1rem;
    }

    .action-btn {
        display: flex;
        flex-direction: column;
        align-items: center;
        background: none;
        border: none;
        padding: 8px;
        color: var(--mat-sys-on-surface-variant);
        cursor: pointer;
        transition: all 0.2s ease;
        flex: 1;
        gap: 4px;
    }

    .action-btn.active {
        color: var(--mat-sys-primary);
    }

    .action-btn mat-icon {
        font-size: 24px;
        width: 24px;
        height: 24px;
    }

    .action-label {
        font-size: 10px;
        font-weight: 600;
        text-transform: uppercase;
        letter-spacing: 0.02em;
    }

    .primary-action-wrapper {
        display: flex;
        flex-direction: column;
        align-items: center;
        margin-top: -30px;
        flex: 1;
        gap: 4px;
    }

    .primary-fab {
        transform: scale(1.1);
        box-shadow: 0 8px 16px rgba(var(--mat-sys-primary), 0.3) !important;
    }

    .primary-label {
        color: var(--mat-sys-primary);
        margin-top: 4px;
    }

    button:disabled {
        opacity: 0.5;
        cursor: not-allowed;
    }
  `]
})
export class MobileActionBarComponent implements OnChanges, OnDestroy {
    @Input() actions: MobileAction[] = [];
    @Input() activeActionId?: string;

    @Output() actionClick = new EventEmitter<MobileAction>();

    private readonly themeService = inject(ThemeService);
    private readonly sidenavService = inject(SidenavService);
    isMobile$ = this.themeService.breakpoints.isMobile$;

    ngOnChanges() {
        (this.sidenavService.mobileActionsVisible as any).set(this.actions.length > 0);
    }

    ngOnDestroy() {
        (this.sidenavService.mobileActionsVisible as any).set(false);
    }


    onActionClick(action: MobileAction) {

        if (!action.disabled) {
            this.actionClick.emit(action);
        }
    }
}
